export const CHAT_SCHEDULE_MAX_PER_USER = 5;
export const CHAT_SCHEDULE_QUESTION_MAX = 2000;
export const CHAT_SCHEDULE_STALE_MS = 20 * 60_000;

export type TimeLocal = {
  hour: number;
  minute: number;
};

export function parseTimeLocal(raw: string): TimeLocal | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour, minute };
}

export function formatTimeLocal(time: TimeLocal): string {
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
}

type ZoneParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function readZoneParts(date: Date, timeZone: string): ZoneParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  const hour = read("hour");
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: hour === 24 ? 0 : hour,
    minute: read("minute"),
  };
}

/** UTC instant that shows as wall clock in `timeZone`. */
export function utcFromZonedLocal(params: {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  timeZone: string;
}): Date {
  let guess = Date.UTC(
    params.year,
    params.month - 1,
    params.day,
    params.hour,
    params.minute,
    0,
  );
  for (let i = 0; i < 3; i += 1) {
    const shown = readZoneParts(new Date(guess), params.timeZone);
    const shownAsUtc = Date.UTC(
      shown.year,
      shown.month - 1,
      shown.day,
      shown.hour,
      shown.minute,
      0,
    );
    const desiredAsUtc = Date.UTC(
      params.year,
      params.month - 1,
      params.day,
      params.hour,
      params.minute,
      0,
    );
    guess += desiredAsUtc - shownAsUtc;
  }
  return new Date(guess);
}

function addCalendarDays(
  parts: Pick<ZoneParts, "year" | "month" | "day">,
  days: number,
): Pick<ZoneParts, "year" | "month" | "day"> {
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

/** Next run strictly after `from` at HH:mm in the schedule time zone. */
export function computeNextRunAt(params: {
  timeLocal: string;
  timeZone: string;
  from: Date;
}): Date {
  const time = parseTimeLocal(params.timeLocal);
  if (!time) {
    throw new Error("INVALID_TIME_LOCAL");
  }
  const today = readZoneParts(params.from, params.timeZone);
  for (let offset = 0; offset <= 2; offset += 1) {
    const day = addCalendarDays(today, offset);
    const candidate = utcFromZonedLocal({
      ...day,
      hour: time.hour,
      minute: time.minute,
      timeZone: params.timeZone,
    });
    if (candidate.getTime() > params.from.getTime()) {
      return candidate;
    }
  }
  const fallbackDay = addCalendarDays(today, 3);
  return utcFromZonedLocal({
    ...fallbackDay,
    hour: time.hour,
    minute: time.minute,
    timeZone: params.timeZone,
  });
}
